<script setup lang="ts">
import { CurrencyCode, type Money, numberFormatFor, parseLocalDate, ratioFromPercent } from '@taler/domain'
import { ActionButton, DecimalInput, Dialog, Select, StatusTag, TextField } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker } from '@/features/entries'
import {
  isMoneyMetric,
  type CreateTargetInput,
  type MetricValue,
  type TargetMetric,
} from '../internal/types.ts'

const props = defineProps<{ ledgerId: string; isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [input: CreateTargetInput] }>()

const { t, locale } = useI18n()

const METRICS: readonly TargetMetric[] = [
  'ACCOUNT_BALANCE',
  'NET_WORTH',
  'MONTHLY_SAVINGS',
  'EXPENSE_INCOME_RATIO',
  'LIABILITY_ASSET_RATIO',
]

const open = ref(false)
const name = ref('')
const metric = ref<TargetMetric>('ACCOUNT_BALANCE')
const currency = ref<CurrencyCode | undefined>(undefined)
const accountId = ref<string | undefined>(undefined)
// A bound is a Money for a money metric, a plain percent string for a ratio one - the two
// never apply at once, but neither type can hold the other, so each metric kind keeps its
// own pair of fields rather than forcing one shape to stand in for both.
const minMoney = ref<Money | null>(null)
const maxMoney = ref<Money | null>(null)
const minRatioText = ref('')
const maxRatioText = ref('')
const goalDateText = ref('')
const validationError = ref<string | undefined>(undefined)

const isMoney = computed(() => isMoneyMetric(metric.value))
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const numberFormat = computed(() => numberFormatFor(currentLocale.value))
const metricOptions = computed(() => METRICS.map((value) => ({ value, label: t(`targets.metric.${value}`) })))
const currencyOptions = Object.values(CurrencyCode).map((value) => ({ value, label: value }))
const currencyChosen = computed(() => isMoney.value && currency.value === undefined)

function clearBounds(): void {
  minMoney.value = null
  maxMoney.value = null
  minRatioText.value = ''
  maxRatioText.value = ''
}

// Changing the metric changes the bounds' unit, so both clear; switching to a ratio
// metric additionally clears the currency, which no longer applies.
watch(metric, (next) => {
  clearBounds()
  if (!isMoneyMetric(next)) currency.value = undefined
  if (next !== 'ACCOUNT_BALANCE') accountId.value = undefined
})

function reset(): void {
  name.value = ''
  metric.value = 'ACCOUNT_BALANCE'
  currency.value = undefined
  accountId.value = undefined
  clearBounds()
  goalDateText.value = ''
  validationError.value = undefined
}

function bound(moneyValue: Money | null, ratioText: string): MetricValue | undefined {
  if (isMoney.value) return moneyValue === null ? undefined : { kind: 'money', value: moneyValue }
  const trimmed = ratioText.trim()
  return trimmed === '' ? undefined : { kind: 'ratio', value: ratioFromPercent(trimmed) }
}

function submit(): void {
  validationError.value = undefined
  if (name.value.trim() === '') return
  if (isMoney.value && currency.value === undefined) {
    validationError.value = t('targets.currencyRequired')
    return
  }
  try {
    const input: { -readonly [K in keyof CreateTargetInput]: CreateTargetInput[K] } = {
      name: name.value.trim(),
      metric: metric.value,
    }
    if (currency.value !== undefined) input.currency = currency.value
    if (accountId.value !== undefined) input.accountId = accountId.value
    const min = bound(minMoney.value, minRatioText.value)
    if (min !== undefined) input.minValue = min
    const max = bound(maxMoney.value, maxRatioText.value)
    if (max !== undefined) input.maxValue = max
    if (goalDateText.value !== '') input.goalDate = parseLocalDate(goalDateText.value)
    emit('create', input)
  } catch {
    validationError.value = t('targets.invalidBound')
  }
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      reset()
      open.value = false
    }
  },
)

const combinedError = computed(() => validationError.value ?? props.errorMessage)
</script>

<template>
  <div>
    <ActionButton type="button" @click="open = true">
      {{ t('targets.addTarget') }}
    </ActionButton>

    <Dialog :open="open" :label="t('targets.addTarget')" @close="open = false">
      <form class="flex w-96 flex-col gap-4 p-[var(--space-block)]" @submit.prevent="submit">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('targets.addTarget') }}
        </h2>
        <TextField v-model="name" :label="t('targets.nameLabel')" maxlength="200" required autofocus />

        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('targets.metricLabel')
          }}</span>
          <Select v-model="metric" :options="metricOptions" :aria-label="t('targets.metricLabel')" />
        </label>

        <label v-if="isMoney" class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('targets.currencyLabel')
          }}</span>
          <Select v-model="currency" :options="currencyOptions" :placeholder="t('targets.selectCurrency')" />
        </label>

        <label v-if="metric === 'ACCOUNT_BALANCE'" class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('targets.accountLabel')
          }}</span>
          <AccountPicker
            id="target-account"
            :label="t('targets.accountLabel')"
            :model-value="accountId"
            :ledger-id="ledgerId"
            include-system
            include-contacts
            @update:model-value="(value) => (accountId = value ?? undefined)"
          />
        </label>

        <div class="grid grid-cols-2 gap-[var(--space-cell)]">
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              isMoney ? t('targets.minValueLabel') : t('targets.minValuePercentLabel')
            }}</span>
            <AmountInput
              v-if="isMoney"
              v-model="minMoney"
              :currency="currency"
              :locale="currentLocale"
              allow-negative
              :disabled="currencyChosen"
              :placeholder="currencyChosen ? t('targets.chooseCurrencyFirst') : undefined"
              :show-total="false"
            />
            <DecimalInput
              v-else
              v-model="minRatioText"
              :decimal-digits="2"
              :group-separator="numberFormat.groupSeparator"
              :decimal-separator="numberFormat.decimalSeparator"
              unit="%"
              allow-negative
              :show-total="false"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
              isMoney ? t('targets.maxValueLabel') : t('targets.maxValuePercentLabel')
            }}</span>
            <AmountInput
              v-if="isMoney"
              v-model="maxMoney"
              :currency="currency"
              :locale="currentLocale"
              allow-negative
              :disabled="currencyChosen"
              :placeholder="currencyChosen ? t('targets.chooseCurrencyFirst') : undefined"
              :show-total="false"
            />
            <DecimalInput
              v-else
              v-model="maxRatioText"
              :decimal-digits="2"
              :group-separator="numberFormat.groupSeparator"
              :decimal-separator="numberFormat.decimalSeparator"
              unit="%"
              allow-negative
              :show-total="false"
            />
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('targets.goalDateLabel')
          }}</span>
          <input v-model="goalDateText" type="date" class="field-control" />
        </label>

        <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

        <div class="flex gap-2">
          <ActionButton type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? t('targets.saving') : t('targets.save') }}
          </ActionButton>
          <ActionButton type="button" variant="secondary" @click="open = false">
            {{ t('targets.cancel') }}
          </ActionButton>
        </div>
      </form>
    </Dialog>
  </div>
</template>
